import { http } from './request'

/** 与后端 PublishStatusEnum / 库表一致 */
export type UserAgreementApiStatus = 'DRAFT' | 'PUBLISHED' | 'OFFLINE'

/** 业务字典 agreement_type 的 dictValue，存于 agreement_type 字段 */
export type UserAgreementApiType = string

export type UserAgreementApiPriority = 'HIGH' | 'MEDIUM' | 'LOW'

const PREFIX = '/biz/overseas/userAgreement'

export interface UserAgreementStatistics {
  totalCount: number
  publishedCount: number
  draftCount: number
  offlineCount: number
}

export interface UserAgreementItem {
  id: string
  title: string
  summary: string
  status: UserAgreementApiStatus
  agreementType: UserAgreementApiType
  priority: UserAgreementApiPriority
  createdAt: string
  updatedAt: string
  creator: string
  agreeCount: number
  viewCount: number
  downloadCount: number
  sortCode?: number
  remark?: string
}

export interface UserAgreementDetail extends UserAgreementItem {
  content: string
}

export interface UserAgreementPageQuery {
  page: number
  pageSize: number
  keyword?: string
  status?: UserAgreementApiStatus
  agreementType?: UserAgreementApiType
  /** 与 yj-admin-ui 用户协议排序选项对应 */
  sort?: 'latest' | 'createTime' | 'views' | 'agrees' | 'sortCode'
}

export interface UserAgreementPageResult {
  list: UserAgreementItem[]
  total: number
}

export interface UserAgreementAddPayload {
  title: string
  summary?: string
  content: string
  agreementType: UserAgreementApiType
  priority?: UserAgreementApiPriority
  status?: UserAgreementApiStatus
  sortCode?: number
  remark?: string
}

export interface UserAgreementEditPayload {
  id: string
  title: string
  summary?: string
  content: string
  agreementType: UserAgreementApiType
  status: UserAgreementApiStatus
  priority: UserAgreementApiPriority
  sortCode?: number
  remark?: string
}

interface SpringPageRaw {
  records?: unknown[]
  total?: number
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function formatDateTime(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number' && Number.isFinite(v)) {
    const d = new Date(v)
    if (!Number.isNaN(d.getTime())) {
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
    }
    return '—'
  }
  const s = String(v).trim()
  if (!s) return '—'
  // ISO / LocalDateTime：2024-05-10T12:34:56 或 2024-05-10 12:34:56（可含毫秒、时区）
  const iso = s.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i,
  )
  if (iso) {
    return `${iso[1]} ${iso[2]}:${iso[3]}:${iso[4]}`
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s} 00:00:00`
  }
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) {
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  }
  return s || '—'
}

function mapRow(raw: Record<string, unknown>): UserAgreementItem {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    summary: String(raw.summary ?? ''),
    status: (raw.status as UserAgreementApiStatus) || 'DRAFT',
    agreementType: (raw.agreementType as UserAgreementApiType) || 'USER_NOTICE',
    priority: (raw.priority as UserAgreementApiPriority) || 'MEDIUM',
    createdAt: formatDateTime(raw.createTime),
    updatedAt: formatDateTime(raw.updateTime),
    creator: String(raw.creatorName ?? '—'),
    agreeCount: Number(raw.agreeCount ?? 0),
    viewCount: Number(raw.viewCount ?? 0),
    downloadCount: Number(raw.downloadCount ?? 0),
    sortCode: raw.sortCode != null ? Number(raw.sortCode) : undefined,
    remark: raw.remark != null ? String(raw.remark) : undefined,
  }
}

function mapDetail(raw: Record<string, unknown>): UserAgreementDetail {
  const base = mapRow(raw)
  return {
    ...base,
    content: String(raw.content ?? ''),
  }
}

function sortToApi(sort: UserAgreementPageQuery['sort']): { sortField: string; sortOrder: string } {
  switch (sort) {
    case 'createTime':
      return { sortField: 'createTime', sortOrder: 'descend' }
    case 'views':
      return { sortField: 'viewCount', sortOrder: 'descend' }
    case 'agrees':
      return { sortField: 'agreeCount', sortOrder: 'descend' }
    case 'sortCode':
      return { sortField: 'sortCode', sortOrder: 'ascend' }
    case 'latest':
    default:
      return { sortField: 'updateTime', sortOrder: 'descend' }
  }
}

/** 将「全部」类筛选值转为 undefined，避免误传给后端 */
function normalizeFilterValue(v?: string): string | undefined {
  const t = (v ?? '').trim()
  if (!t) return undefined
  if (t === 'ALL' || t === '全部' || t === '全部状态' || t === '全部类型') return undefined
  return t
}

export async function fetchUserAgreementStatistics(): Promise<UserAgreementStatistics> {
  const raw = await http.get<Record<string, unknown>>(`${PREFIX}/statistics`)
  return {
    totalCount: Number(raw.totalCount ?? 0),
    publishedCount: Number(raw.publishedCount ?? 0),
    draftCount: Number(raw.draftCount ?? 0),
    offlineCount: Number(raw.offlineCount ?? 0),
  }
}

export async function fetchUserAgreementPage(
  query: UserAgreementPageQuery,
): Promise<UserAgreementPageResult> {
  const { sortField, sortOrder } = sortToApi(query.sort)
  const params: Record<string, string | number | undefined> = {
    current: query.page,
    size: query.pageSize,
    searchKey: query.keyword?.trim() || undefined,
    status: normalizeFilterValue(query.status),
    agreementType: normalizeFilterValue(query.agreementType),
    sortField,
    sortOrder,
  }
  const raw = await http.get<SpringPageRaw>(`${PREFIX}/page`, { params })
  const rows = Array.isArray(raw.records) ? raw.records : []
  const list = rows.map((r) => mapRow(r as Record<string, unknown>)).filter((x) => x.id)
  const total = typeof raw.total === 'number' ? raw.total : 0
  return { list, total }
}

export async function fetchUserAgreementDetail(id: string): Promise<UserAgreementDetail | null> {
  if (!id) return null
  const raw = await http.get<Record<string, unknown>>(`${PREFIX}/detail`, { params: { id } })
  if (!raw || typeof raw !== 'object' || !raw.id) return null
  return mapDetail(raw)
}

export async function createUserAgreement(payload: UserAgreementAddPayload): Promise<void> {
  await http.post<void>(`${PREFIX}/add`, {
    title: payload.title.trim(),
    summary: payload.summary?.trim() || undefined,
    content: payload.content,
    agreementType: payload.agreementType,
    priority: payload.priority ?? 'MEDIUM',
    status: payload.status ?? 'DRAFT',
    sortCode: payload.sortCode ?? 0,
    remark: payload.remark?.trim() || undefined,
  })
}

export interface UserAgreementImportFailItem {
  file?: string
  row: number
  title?: string
  message: string
}

export interface UserAgreementImportResult {
  totalCount: number
  successCount: number
  errorCount: number
  errorDetail: UserAgreementImportFailItem[]
}

/** GET /biz/overseas/userAgreement/importTemplate */
export async function downloadUserAgreementImportTemplate(): Promise<void> {
  await http.download(`${PREFIX}/importTemplate`, '用户协议批量导入模板.xlsx')
}

/**
 * POST /biz/overseas/userAgreement/import
 * multipart 字段名 files，一次最多 5 个 Excel
 */
export async function importUserAgreementFiles(files: File[]): Promise<UserAgreementImportResult> {
  if (!files.length) {
    throw new Error('请至少选择 1 个文件')
  }
  if (files.length > 5) {
    throw new Error('一次最多上传 5 个文件')
  }
  const fd = new FormData()
  for (const f of files) {
    fd.append('files', f)
  }
  const raw = await http.upload<Record<string, unknown>>(`${PREFIX}/import`, fd)
  const o = raw && typeof raw === 'object' ? raw : {}
  const detailRaw = Array.isArray(o.errorDetail) ? o.errorDetail : []
  const errorDetail: UserAgreementImportFailItem[] = detailRaw.map((item) => {
    const r = item && typeof item === 'object' ? (item as Record<string, unknown>) : {}
    return {
      file: r.file != null ? String(r.file) : undefined,
      row: Number(r.row) || 0,
      title: r.title != null ? String(r.title) : undefined,
      message: String(r.message ?? '导入失败'),
    }
  })
  return {
    totalCount: typeof o.totalCount === 'number' ? o.totalCount : 0,
    successCount: typeof o.successCount === 'number' ? o.successCount : 0,
    errorCount: typeof o.errorCount === 'number' ? o.errorCount : errorDetail.length,
    errorDetail,
  }
}

export async function updateUserAgreement(payload: UserAgreementEditPayload): Promise<void> {
  await http.post<void>(`${PREFIX}/edit`, {
    id: payload.id,
    title: payload.title.trim(),
    summary: payload.summary?.trim() || undefined,
    content: payload.content,
    agreementType: payload.agreementType,
    status: payload.status,
    priority: payload.priority,
    sortCode: payload.sortCode ?? 0,
    remark: payload.remark?.trim() || undefined,
  })
}

export async function deleteUserAgreement(id: string): Promise<void> {
  await http.post<void>(`${PREFIX}/delete`, { id })
}

export async function publishUserAgreement(id: string): Promise<void> {
  await http.post<void>(`${PREFIX}/publish`, { id })
}

export async function offlineUserAgreement(id: string): Promise<void> {
  await http.post<void>(`${PREFIX}/offline`, { id })
}

/** 返回新草稿主键 */
export async function copyUserAgreement(id: string): Promise<string | null> {
  const data = await http.post<string>(`${PREFIX}/copy`, { id })
  return data != null && data !== '' ? String(data) : null
}

/**
 * 按当前筛选条件导出：每份协议一个 Word（保留富文本），多份打包为 ZIP。
 * GET /biz/overseas/userAgreement/export
 */
export async function exportUserAgreementWordZip(
  query: Pick<UserAgreementPageQuery, 'keyword' | 'status' | 'agreementType' | 'sort'>,
): Promise<void> {
  const { sortField, sortOrder } = sortToApi(query.sort)
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const filename = `用户协议导出_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}.zip`
  const blob = await http.getBlob(`${PREFIX}/export`, {
    params: {
      searchKey: query.keyword?.trim() || undefined,
      status: normalizeFilterValue(query.status),
      agreementType: normalizeFilterValue(query.agreementType),
      sortField,
      sortOrder,
    },
    timeout: 120_000,
  })
  // 业务失败时后端可能返回 JSON（仍 200），避免误存为 zip
  const head = await blob.slice(0, 1).text()
  if (head === '{' || (blob.type && blob.type.includes('json'))) {
    const text = await blob.text()
    let message = '导出失败'
    try {
      const json = JSON.parse(text) as { msg?: string; message?: string }
      message = json.msg || json.message || message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
